import React, { useMemo } from "react"
import withRouter from "components/Common/withRouter"
import { useTranslation, withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import TableContainer from "components/Common/TableContainer"
import { OrderRow } from "./Revenue"

interface RevenueTableProps {
  revenueData: OrderRow[]
  isAllChecked: boolean
  checkedRows: OrderRow[]
  toggleAllRows: () => void
  checkedRowHandler: (row: OrderRow) => void
  activeTab: string
}

const RevenueTable: React.FC<RevenueTableProps> = ({
  revenueData,
  isAllChecked,
  checkedRows,
  toggleAllRows,
  checkedRowHandler,
  activeTab,
}) => {
  const { t } = useTranslation()

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <input
            type="checkbox"
            className="form-check-input"
            onClick={toggleAllRows}
            checked={isAllChecked}
          />
        ),
        accessor: "id",
        Cell: (cellProps: { row: { original: OrderRow } }) => {
          return (
            <input
              onClick={() => checkedRowHandler(cellProps.row.original)}
              checked={Boolean(
                checkedRows.find(row => row.orderDate === cellProps.row.original.orderDate)
              )}
              type="checkbox"
              className="form-check-input"
            />
          )
        },
      },
      ...(activeTab === "2"
        ? [
            {
              Header: t("revenue_orderId"),
              accessor: "orderId",
              filterable: true,
              Cell: (cellProps: { row: { original: OrderRow } }) => {
                return <>{cellProps.row.original.orderId}</>
              },
            },
            {
              Header: t("revenue_orderDate"),
              accessor: "orderDate",
              filterable: true,
              Cell: (cellProps: { row: { original: OrderRow } }) => {
                return <>{cellProps.row.original.orderDate}</>
              },
            },
          ]
        : [
            {
              Header: t("revenue_orderDate"),
              accessor: "orderDate",
              filterable: true,
              Cell: (cellProps: { row: { original: OrderRow } }) => {
                return <>{cellProps.row.original.orderDate}</>
              },
            },
            {
              Header: t("revenue_totalOrders"),
              accessor: "totalOrders",
              filterable: true,
              Cell: (cellProps: { row: { original: OrderRow } }) => {
                return <>{cellProps.row.original.totalOrders} {t("revenue_order")}</>
              },
            },
          ]),
      {
        Header: t("revenue_revenue"),
        accessor: "revenueQAR",
        filterable: true,
        Cell: (cellProps: { row: { original: OrderRow } }) => {
          return <>{cellProps.row.original.revenueQAR.toLocaleString()} {t("common_qar")}</>
        },
      },
      {
        Header: t("revenue_earnings"),
        accessor: "earningsQAR",
        filterable: true,
        Cell: (cellProps: { row: { original: OrderRow } }) => {
          return <>{cellProps.row.original.earningsQAR.toLocaleString()} {t("common_qar")}</>
        },
      },
      {
        Header: t("revenue_commission"),
        accessor: "commissionQAR",
        filterable: true,
        Cell: (cellProps: { row: { original: OrderRow } }) => {
          return <>{cellProps.row.original.commissionQAR.toLocaleString()} {t("common_qar")}</>
        },
      },
      {
        Header: t("revenue_status"),
        accessor: "status",
        filterable: true,
        Cell: (cellProps: { row: { original: OrderRow } }) => {
          const status = cellProps.row.original.status
          const statusClass =
            status === "Collected"
              ? "bg-cyan-30 text-cyan"
              : "bg-danger-30 text-danger"

          return (
            <span
              className={
                statusClass + " px-2 py-1 rounded fw-semibold font-size-12"
              }
            >
              {status === "Collected"
                ? t("revenue_collectedStatus")
                : t("revenue_notCollectedStatus")}
            </span>
          )
        },
      },
    ],
    [revenueData, isAllChecked, checkedRows, activeTab]
  )

  return (
    <TableContainer
      columns={columns}
      data={revenueData}
      isGlobalFilter={true}
      isAddOptions={false}
      customPageSize={5}
      isJobListGlobalFilter={undefined}
      isAddUserList={undefined}
      handleOrderClicks={undefined}
      handleUserClick={undefined}
      handleCustomerClick={undefined}
      isAddCustList={undefined}
      className={undefined}
      customPageSizeOptions={undefined}
    />
  )
}

RevenueTable.propTypes = {
  revenueData: PropTypes.array.isRequired,
  isAllChecked: PropTypes.bool.isRequired,
  checkedRows: PropTypes.array.isRequired,
  toggleAllRows: PropTypes.func.isRequired,
  checkedRowHandler: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(RevenueTable))